<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PhotoGalleryOverlay                                   |
| Section: components                                              |
| Purpose: Overlay gallery viewer for photo navigation and zoom.   |
+------------------------------------------------------------------+
-->
<template>
    <div v-if="isVisible" class="overlay" @click="closeOverlay">
        <div ref="dialog" class="overlay-dialog" role="dialog" aria-modal="true"
            aria-labelledby="photo-gallery-overlay-title" @click.stop>
            <MediaOverlayHeader ref="header" :title="currentMediaTitle" media-type="image" media-kind="photo"
                :owner="currentMediaOwner" :linked-person-ids="currentMediaLinkedPersonIDs"
                :current-index="-1" :total-items="0" heading-id="photo-gallery-overlay-title"
                details-id="photo-gallery-overlay-details" @close="closeOverlay" />

            <div class="overlay-content" @mousedown="startDrag" @mousemove="onDrag" @mouseup="endDrag"
                @mouseleave="endDrag" @wheel="onWheelZoom">
                <div class="zoom-controls">
                    <button type="button" :aria-label="$t('mediaOverlay.zoomIn')" @click="zoomIn">
                        <img src="@/assets/icons/zoom_in_white.svg" alt="" aria-hidden="true">
                    </button>
                    <button type="button" :aria-label="$t('mediaOverlay.zoomOut')" @click="zoomOut">
                        <img src="@/assets/icons/zoom_out_white.svg" alt="" aria-hidden="true">
                    </button>
                </div>

                <img ref="mediaElement" class="mediaElement" :src="currentPhotoUrl"
                    :alt="currentMediaTitle || $t('mediaOverlay.untitled')" draggable="false">
            </div>
        </div>
    </div>
</template>

<script>
import MediaOverlayHeader from '@/components/MediaOverlayHeader.vue';
import { getPhotoPath, normalizeOverlayMedia } from '@/utils/mediaUtils';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

export default {
    name: 'PhotoGalleryOverlay',
    components: { MediaOverlayHeader },
    emits: ['close'],
    props: {
        isVisible: { type: Boolean, required: true },
        photos: { type: Array, required: true },
        mediaTitle: { type: String, default: '' },
        mediaOwner: { type: String, default: '' }
    },
    data() {
        return {
            currentPhotoUrl: '',
            currentMediaTitle: '',
            currentMediaOwner: '',
            currentMediaLinkedPersonIDs: [],
            currentOverlayMedia: null,
            scale: 1,
            isDragging: false,
            startX: 0,
            startY: 0,
            translateX: 0,
            translateY: 0,
            previouslyFocusedElement: null
        };
    },
    watch: {
        isVisible(newValue) {
            if (newValue) {
                this.openDialog();
            } else {
                this.removeNoScrollClass();
                this.restoreFocus();
            }
        },
        photos: {
            handler(newPhotos) {
                if (newPhotos.length) this.setCurrentPhoto(newPhotos[0]);
            }
        }
    },
    created() {
        if (this.photos.length) this.setCurrentPhoto(this.photos[0]);
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
        if (this.isVisible) this.openDialog();
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.removeNoScrollClass();
        this.restoreFocus();
    },
    methods: {
        setCurrentPhoto(photo) {
            const overlayMedia = normalizeOverlayMedia(photo, {
                type: 'image',
                kind: 'photo',
                pathResolver: getPhotoPath,
                defaultTitle: this.mediaTitle,
                defaultOwner: this.mediaOwner
            });

            this.currentOverlayMedia = overlayMedia;
            this.currentPhotoUrl = overlayMedia.url;
            this.currentMediaTitle = overlayMedia.title;
            this.currentMediaOwner = overlayMedia.owner;
            this.currentMediaLinkedPersonIDs = overlayMedia.linkedPersonIDs;
            this.resetTransform();
        },
        openDialog() {
            if (!this.previouslyFocusedElement) {
                this.previouslyFocusedElement = document.activeElement;
            }
            this.addNoScrollClass();
            this.$nextTick(() => this.$refs.header?.focusCloseButton());
        },
        closeOverlay() {
            this.$refs.header?.closeDetails({ restoreFocus: false });
            this.removeNoScrollClass();
            this.$emit('close');
        },
        restoreFocus() {
            const target = this.previouslyFocusedElement;
            if (target && document.contains(target) && typeof target.focus === 'function') {
                target.focus();
            }
            this.previouslyFocusedElement = null;
        },
        handleKeydown(event) {
            if (!this.isVisible) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                if (!this.$refs.header?.closeDetails()) this.closeOverlay();
            } else if (event.key === 'Tab') {
                this.trapFocus(event);
            }
        },
        trapFocus(event) {
            const focusableElements = [...(this.$refs.dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || [])]
                .filter((element) => element.getClientRects().length > 0);
            if (!focusableElements.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;

            if (event.shiftKey && (activeElement === firstElement || !this.$refs.dialog.contains(activeElement))) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && (activeElement === lastElement || !this.$refs.dialog.contains(activeElement))) {
                event.preventDefault();
                firstElement.focus();
            }
        },
        startDrag(event) {
            if (event.button !== 0 || event.target.closest('button')) return;
            this.isDragging = true;
            this.startX = event.clientX - this.translateX;
            this.startY = event.clientY - this.translateY;
        },
        onDrag(event) {
            if (!this.isDragging) return;
            this.translateX = event.clientX - this.startX;
            this.translateY = event.clientY - this.startY;
            this.updateTransform();
        },
        endDrag() {
            this.isDragging = false;
        },
        onWheelZoom(event) {
            event.preventDefault();
            this.adjustScale(event.deltaY * -0.001);
        },
        zoomIn() {
            this.adjustScale(0.1);
        },
        zoomOut() {
            this.adjustScale(-0.1);
        },
        adjustScale(change) {
            this.scale = Math.min(Math.max(this.scale + change, 0.5), 4);
            this.updateTransform();
        },
        updateTransform() {
            if (!this.$refs.mediaElement) return;
            this.$refs.mediaElement.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
        },
        resetTransform() {
            this.scale = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.isDragging = false;
            this.$nextTick(() => this.updateTransform());
        },
        addNoScrollClass() {
            document.body.classList.add('no-scroll');
        },
        removeNoScrollClass() {
            document.body.classList.remove('no-scroll');
        }
    }
};
</script>

<style scoped>
.overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(29, 29, 29, 0.96);
}

.overlay-dialog {
    display: flex;
    flex-direction: column;
    width: calc(100% - 96px);
    height: calc(100% - 96px);
    overflow: visible;
    border: 2px solid rgba(255, 255, 255, 0.24);
    background: var(--brand-bg-4);
}

.overlay-content {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-height: 0;
    overflow: hidden;
    cursor: grab;
    user-select: none;
    -webkit-user-drag: none;
}

.overlay-content:active {
    cursor: grabbing;
}

.mediaElement {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    transform: translate(0, 0) scale(1);
}

.zoom-controls {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.zoom-controls button {
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: var(--border-radius-pill);
    cursor: pointer;
    transition: var(--transition-bounce);
}

.zoom-controls button:hover,
.zoom-controls button:focus-visible {
    box-shadow: 0 0 0 4px var(--brand-bg-1);
    outline: none;
}

@media (max-width: 860px) {
    .overlay-dialog {
        width: 100%;
        height: 100%;
        border: 0;
    }
}
</style>
